import type { MenuScreen, Scene } from "../types";
import { el } from "./dom";
import { createScreenShell } from "./Screen";
import { createCrestButton } from "./CrestButton";
import { createDivider } from "./Divider";
import { createMotifWrap } from "./Motif";
import { mountMenuCorners } from "./MenuCorners";
import { t } from "@bn-games/i18n-core";

export interface MainMenuScreenOptions {
  readonly version: string;
  /**
   * Called when the user taps "Quick Start". Apps wire this to launch a
   * vs-AI game directly using sticky settings (color / difficulty / variant),
   * bypassing PlayMenu entirely. The "New Game" button still routes through
   * PlayMenu for users who want to pick mode or play online.
   */
  readonly onQuickStart: () => void;
}

interface MinimalContext {
  navigate(scene: Scene): void;
}

/**
 * Shared main-menu screen — title block, Quick Start / New Game / Rules /
 * Settings / History / About buttons, footer motif with version string, plus
 * the auth widget (top-right corner) and a back-to-landing link (top-left
 * corner) via `mountMenuCorners` (see there for its own Discord-Activity /
 * Capacitor auto-hiding). The per-app `MenuContext` extends `MinimalContext`,
 * so this factory is parametric over the consumer's context type without
 * requiring a separate adapter at each call site.
 */
export function createMainMenuScreen<TContext extends MinimalContext>(
  opts: MainMenuScreenOptions,
): MenuScreen<TContext> {
  let frame: HTMLElement | null = null;
  let corners: ReturnType<typeof mountMenuCorners> | null = null;

  return {
    mount(root, ctx) {
      const shell = createScreenShell();
      frame = shell.frame;
      const body = shell.body;

      const titleBlock = el("div", { class: "menu-subtitle" }, [
        el("div", { class: "main-title", text: t("app.name") }),
        el("div", { class: "main-title-divider" }, [createDivider()]),
      ]);

      const buttons = el("div", { class: "button-stack" }, [
        createCrestButton({ label: t("mainMenu.quickStart"), primary: true, onClick: () => opts.onQuickStart() }),
        createCrestButton({ label: t("mainMenu.newGame"), onClick: () => ctx.navigate("play") }),
        createCrestButton({ label: t("mainMenu.rules"), onClick: () => ctx.navigate("rules") }),
        createCrestButton({ label: t("mainMenu.settings"), onClick: () => ctx.navigate("settings") }),
        createCrestButton({ label: t("mainMenu.history"), onClick: () => ctx.navigate("history") }),
        createCrestButton({ label: t("mainMenu.about"), onClick: () => ctx.navigate("about") }),
      ]);

      const footer = el("div", { class: "footer-motif" }, [
        createMotifWrap(),
        el("div", { class: "footer-motif__version", text: `v ${opts.version}` }),
      ]);

      // Title + footer stay pinned at top/bottom; the buttons live in the
      // scrollable middle so a short case-frame can still reach every button.
      const middle = el("div", { class: "about-center" }, [buttons]);

      body.appendChild(titleBlock);
      body.appendChild(middle);
      body.appendChild(footer);
      root.appendChild(frame);

      corners = mountMenuCorners(root);
    },
    unmount() {
      corners?.destroy();
      corners = null;
      frame?.remove();
      frame = null;
    },
  };
}
